import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useProfile } from '../contexts/ProfileContext';
import { useAuth } from '../contexts/AuthContext';

// Types for Cloud Functions responses
interface CreateUserResponse {
  success: boolean;
  userId?: string;
  message?: string;
  error?: string;
}

interface GetUserResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    userType: string;
    isActive: boolean;
    createdAt: Date | string;
    updatedAt?: Date | string;
  };
  error?: string;
}

interface UpdateUserResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    userType: string;
    isActive: boolean;
    createdAt: Date | string;
    updatedAt?: Date | string;
  };
  error?: string;
}

interface DeleteUserResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface CreateStartupProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    createdAt: Date | string;
  };
  message?: string;
  error?: string;
}

interface UpdateStartupProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    updatedAt: Date | string;
  };
  message?: string;
  error?: string;
}

interface GetStartupProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    createdAt: Date | string;
    updatedAt: Date | string;
  };
  message?: string;
  error?: string;
}

interface CreateInvestorProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    createdAt: Date | string;
  };
  message?: string;
  error?: string;
}

interface UpdateInvestorProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    updatedAt: Date | string;
  };
  message?: string;
  error?: string;
}

interface GetInvestorProfileResponse {
  success: boolean;
  profile?: {
    id: string;
    userId: string;
    companyName: string;
    currentStep: number;
    profileCompleted: boolean;
    createdAt: Date | string;
    updatedAt: Date | string;
  };
  message?: string;
  error?: string;
}

const CloudFunctionsTest: React.FC = () => {
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [testUser, setTestUser] = useState<{
    id: string;
    email: string;
    userType: string;
  } | null>(null);

  const [testInvestorUser, setTestInvestorUser] = useState<{
    id: string;
    email: string;
    userType: string;
  } | null>(null);

  const [testStartupId, setTestStartupId] = useState<string>('');

  // Profile context
  const {
    startupProfile,
    investorProfile,
    loading: profileLoading,
    error: profileError,
    createProfile,
    updateProfile,
  } = useProfile();

  // Auth context
  const { user: authUser, firebaseUser } = useAuth();

  const functions = getFunctions();

  const addResult = (message: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Helper function to get the userId to use for tracking
  const getTrackingUserId = (): string | null => {
    if (testInvestorUser) return testInvestorUser.id;
    if (authUser && authUser.userType === 'investor' && firebaseUser) return authUser.id;
    return null;
  };

  // Helper function to check if tracking is possible
  const canTrack = (): boolean => {
    return !!firebaseUser && !!authUser && authUser.userType === 'investor';
  };

  const clearResults = () => {
    setResults([]);
    setTestUser(null);
    setTestInvestorUser(null);
  };

  // Test CREATE USER
  const testCreateUser = async () => {
    setLoading(true);
    try {
      const createUser = httpsCallable(functions, 'createUser');
      const testEmail = `test-${Date.now()}@example.com`;

      const result = await createUser({
        email: testEmail,
        password: 'testpass123',
        userType: 'startup',
      });

      const data = result.data as CreateUserResponse;
      if (data.success && data.userId) {
        addResult(`✅ CREATE: Usuario creado - ID: ${data.userId}`);
        setTestUser({ id: data.userId, email: testEmail, userType: 'startup' });
      } else {
        addResult(`❌ CREATE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ CREATE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test GET USER
  const testGetUser = async () => {
    if (!testUser) {
      addResult('❌ GET: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const getUser = httpsCallable(functions, 'getUser');
      const result = await getUser({ userId: testUser.id });

      const data = result.data as GetUserResponse;
      if (data.success) {
        addResult(
          `✅ GET: Usuario obtenido - Email: ${data.user?.email}, Tipo: ${data.user?.userType}`
        );
      } else {
        addResult(`❌ GET: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ GET: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test UPDATE USER
  const testUpdateUser = async () => {
    if (!testUser) {
      addResult('❌ UPDATE: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const updateUser = httpsCallable(functions, 'updateUser');
      const result = await updateUser({
        userId: testUser.id,
        updates: {
          name: 'Usuario de Prueba',
          bio: 'Este es un usuario creado para testing',
        },
      });

      const data = result.data as UpdateUserResponse;
      if (data.success) {
        addResult(`✅ UPDATE: Usuario actualizado - Nombre: ${data.user?.name}`);
      } else {
        addResult(`❌ UPDATE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ UPDATE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test DELETE USER
  const testDeleteUser = async () => {
    if (!testUser) {
      addResult('❌ DELETE: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const deleteUser = httpsCallable(functions, 'deleteUser');
      const result = await deleteUser({ userId: testUser.id });

      const data = result.data as DeleteUserResponse;
      if (data.success) {
        addResult(`✅ DELETE: Usuario eliminado - ${data.message}`);
        setTestUser(null);
      } else {
        addResult(`❌ DELETE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ DELETE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test CREATE STARTUP PROFILE
  const testCreateStartupProfile = async () => {
    if (!testUser) {
      addResult('❌ CREATE PROFILE: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const createStartupProfile = httpsCallable(functions, 'createStartupProfile');
      const result = await createStartupProfile({
        userId: testUser.id,
        initialData: {
          companyName: 'Mi Startup de Prueba',
          country: 'Chile',
        },
      });

      const data = result.data as CreateStartupProfileResponse;
      if (data.success) {
        addResult(
          `✅ CREATE PROFILE: Perfil creado - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}`
        );
      } else {
        addResult(`❌ CREATE PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ CREATE PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test UPDATE STARTUP PROFILE
  const testUpdateStartupProfile = async () => {
    if (!testUser) {
      addResult('❌ UPDATE PROFILE: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const updateStartupProfile = httpsCallable(functions, 'updateStartupProfile');
      const result = await updateStartupProfile({
        userId: testUser.id,
        updates: {
          companyName: 'Mi Startup Actualizada',
          slogan: 'Innovando el futuro',
          currentStep: 1,
        },
        stepNumber: 1,
      });

      const data = result.data as UpdateStartupProfileResponse;
      if (data.success) {
        addResult(
          `✅ UPDATE PROFILE: Perfil actualizado - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}`
        );
      } else {
        addResult(`❌ UPDATE PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ UPDATE PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test GET STARTUP PROFILE
  const testGetStartupProfile = async () => {
    if (!testUser) {
      addResult('❌ GET PROFILE: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
      const result = await getStartupProfile({
        userId: testUser.id,
      });

      const data = result.data as GetStartupProfileResponse;
      if (data.success) {
        addResult(
          `✅ GET PROFILE: Perfil obtenido - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}, Completado: ${data.profile?.profileCompleted}`
        );
      } else {
        addResult(`❌ GET PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ GET PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test CREATE INVESTOR PROFILE
  const testCreateInvestorProfile = async () => {
    if (!testInvestorUser) {
      addResult(
        '❌ CREATE INVESTOR PROFILE: No hay usuario inversor de prueba. Crea uno primero con userType: "investor".'
      );
      return;
    }

    setLoading(true);
    try {
      const createInvestorProfile = httpsCallable(functions, 'createInvestorProfile');
      const result = await createInvestorProfile({
        userId: testInvestorUser.id,
        initialData: {
          companyName: 'Mi Fondo de Inversión',
          country: 'Chile',
        },
      });

      const data = result.data as CreateInvestorProfileResponse;
      if (data.success) {
        addResult(
          `✅ CREATE INVESTOR PROFILE: Perfil creado - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}`
        );
      } else {
        addResult(`❌ CREATE INVESTOR PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ CREATE INVESTOR PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test UPDATE INVESTOR PROFILE
  const testUpdateInvestorProfile = async () => {
    if (!testInvestorUser) {
      addResult('❌ UPDATE INVESTOR PROFILE: No hay usuario inversor de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const updateInvestorProfile = httpsCallable(functions, 'updateInvestorProfile');
      const result = await updateInvestorProfile({
        userId: testInvestorUser.id,
        updates: {
          companyName: 'Mi Fondo Actualizado',
          previousInvestments: 'He invertido en múltiples startups tech',
          currentStep: 1,
        },
        stepNumber: 1,
      });

      const data = result.data as UpdateInvestorProfileResponse;
      if (data.success) {
        addResult(
          `✅ UPDATE INVESTOR PROFILE: Perfil actualizado - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}`
        );
      } else {
        addResult(`❌ UPDATE INVESTOR PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ UPDATE INVESTOR PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test GET INVESTOR PROFILE
  const testGetInvestorProfile = async () => {
    if (!testInvestorUser) {
      addResult('❌ GET INVESTOR PROFILE: No hay usuario inversor de prueba. Crea uno primero.');
      return;
    }

    setLoading(true);
    try {
      const getInvestorProfile = httpsCallable(functions, 'getInvestorProfile');
      const result = await getInvestorProfile({
        userId: testInvestorUser.id,
      });

      const data = result.data as GetInvestorProfileResponse;
      if (data.success) {
        addResult(
          `✅ GET INVESTOR PROFILE: Perfil obtenido - Empresa: ${data.profile?.companyName}, Step: ${data.profile?.currentStep}, Completado: ${data.profile?.profileCompleted}`
        );
      } else {
        addResult(`❌ GET INVESTOR PROFILE: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ GET INVESTOR PROFILE: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test CREATE INVESTOR USER
  const testCreateInvestorUser = async () => {
    setLoading(true);
    try {
      const createUser = httpsCallable(functions, 'createUser');
      const testEmail = `investor-test-${Date.now()}@example.com`;

      const result = await createUser({
        email: testEmail,
        password: 'testpass123',
        userType: 'investor',
      });

      const data = result.data as CreateUserResponse;
      if (data.success && data.userId) {
        addResult(`✅ CREATE INVESTOR: Usuario creado - ID: ${data.userId}`);
        setTestInvestorUser({
          id: data.userId,
          email: testEmail,
          userType: 'investor',
        });
      } else {
        addResult(`❌ CREATE INVESTOR: Error - ${data.error}`);
      }
    } catch (error) {
      addResult(`❌ CREATE INVESTOR: Error - ${error}`);
    }
    setLoading(false);
  };

  // Test PROFILE CONTEXT (Startup)
  const testProfileContext = async () => {
    if (!testUser) {
      addResult('❌ PROFILE CONTEXT: No hay usuario de prueba. Crea uno primero.');
      return;
    }

    try {
      addResult('🔄 PROFILE CONTEXT: Creando perfil usando ProfileContext...');
      await createProfile(testUser.id, 'startup', {
        companyName: 'Mi Startup via Context',
        country: 'Chile',
      });
      addResult('✅ PROFILE CONTEXT: Perfil creado exitosamente');

      addResult('🔄 PROFILE CONTEXT: Actualizando perfil usando ProfileContext...');
      await updateProfile(
        testUser.id,
        'startup',
        {
          companyName: 'Mi Startup Actualizada via Context',
          slogan: 'Innovando desde el contexto',
        },
        1
      );
      addResult('✅ PROFILE CONTEXT: Perfil actualizado exitosamente');

      addResult(
        `📊 PROFILE CONTEXT: Estado actual - Empresa: ${startupProfile?.companyName}, Step: ${startupProfile?.currentStep}`
      );
    } catch (error: unknown) {
      addResult(
        `❌ PROFILE CONTEXT: Error - ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Test INVESTOR PROFILE CONTEXT
  const testInvestorProfileContext = async () => {
    if (!testInvestorUser) {
      addResult(
        '❌ INVESTOR PROFILE CONTEXT: No hay usuario inversor de prueba. Crea uno primero.'
      );
      return;
    }

    try {
      addResult('🔄 INVESTOR PROFILE CONTEXT: Creando perfil usando ProfileContext...');
      await createProfile(testInvestorUser.id, 'investor', {
        companyName: 'Mi Fondo via Context',
        country: 'Chile',
      });
      addResult('✅ INVESTOR PROFILE CONTEXT: Perfil creado exitosamente');

      addResult('🔄 INVESTOR PROFILE CONTEXT: Actualizando perfil usando ProfileContext...');
      await updateProfile(
        testInvestorUser.id,
        'investor',
        {
          companyName: 'Mi Fondo Actualizado via Context',
          previousInvestments: 'He invertido en múltiples startups',
        },
        1
      );
      addResult('✅ INVESTOR PROFILE CONTEXT: Perfil actualizado exitosamente');

      addResult(
        `📊 INVESTOR PROFILE CONTEXT: Estado actual - Empresa: ${investorProfile?.companyName}, Step: ${investorProfile?.currentStep}`
      );
    } catch (error: unknown) {
      addResult(
        `❌ INVESTOR PROFILE CONTEXT: Error - ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Test ALL CRUD operations
  const testAllCRUD = async () => {
    clearResults();
    addResult('🚀 Iniciando test completo del CRUD...');

    // CREATE
    await testCreateUser();
    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second

    // GET
    await testGetUser();
    await new Promise(resolve => setTimeout(resolve, 1000));

    // UPDATE
    await testUpdateUser();
    await new Promise(resolve => setTimeout(resolve, 1000));

    // GET again to verify update
    await testGetUser();
    await new Promise(resolve => setTimeout(resolve, 1000));

    // DELETE
    await testDeleteUser();

    addResult('🎉 Test completo del CRUD finalizado!');
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">🧪 Test de Cloud Functions CRUD</h2>

      <div className="mb-6">
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={testCreateUser}
            disabled={loading}
            className="px-4 py-2 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50"
          >
            CREATE User
          </button>
          <button
            onClick={testGetUser}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            GET User
          </button>
          <button
            onClick={testUpdateUser}
            disabled={loading}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
          >
            UPDATE User
          </button>
          <button
            onClick={testDeleteUser}
            disabled={loading}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
          >
            DELETE User
          </button>
          <button
            onClick={testCreateStartupProfile}
            disabled={loading}
            className="px-4 py-2 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50"
          >
            CREATE STARTUP PROFILE
          </button>
          <button
            onClick={testUpdateStartupProfile}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            UPDATE STARTUP PROFILE
          </button>
          <button
            onClick={testGetStartupProfile}
            disabled={loading}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
          >
            GET STARTUP PROFILE
          </button>
          <button
            onClick={testProfileContext}
            disabled={loading || profileLoading}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
          >
            TEST PROFILE CONTEXT
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 mt-4 pt-4 border-t-2 border-gray-300">
          <h3 className="w-full text-lg font-semibold text-gray-700 mb-2">
            🔵 Funciones de Inversor
          </h3>
          <button
            onClick={testCreateInvestorUser}
            disabled={loading}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
          >
            CREATE INVESTOR USER
          </button>
          <button
            onClick={testCreateInvestorProfile}
            disabled={loading}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50"
          >
            CREATE INVESTOR PROFILE
          </button>
          <button
            onClick={testUpdateInvestorProfile}
            disabled={loading}
            className="px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 disabled:opacity-50"
          >
            UPDATE INVESTOR PROFILE
          </button>
          <button
            onClick={testGetInvestorProfile}
            disabled={loading}
            className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50"
          >
            GET INVESTOR PROFILE
          </button>
          <button
            onClick={testInvestorProfileContext}
            disabled={loading || profileLoading}
            className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
          >
            TEST INVESTOR PROFILE CONTEXT
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={testAllCRUD}
            disabled={loading}
            className="px-6 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 font-semibold"
          >
            🚀 Test Completo CRUD
          </button>
          <button
            onClick={clearResults}
            disabled={loading}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
          >
            Limpiar
          </button>
        </div>
      </div>

      {testUser && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-800">
            <strong>Usuario Startup de prueba:</strong> {testUser.email} (ID: {testUser.id})
          </p>
        </div>
      )}

      {testInvestorUser && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
          <p className="text-sm text-indigo-800">
            <strong>Usuario Inversor de prueba:</strong> {testInvestorUser.email} (ID:{' '}
            {testInvestorUser.id})
          </p>
        </div>
      )}

      {/* Profile Context Status */}
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
        <p className="text-sm text-green-800">
          <strong>ProfileContext Status:</strong>
        </p>
        <p className="text-xs text-green-700">
          Startup Profile:{' '}
          {startupProfile
            ? `${startupProfile.companyName} (Step ${startupProfile.currentStep})`
            : 'No profile'}
        </p>
        <p className="text-xs text-green-700">
          Investor Profile:{' '}
          {investorProfile
            ? `${investorProfile.companyName} (Step ${investorProfile.currentStep})`
            : 'No profile'}
        </p>
        <p className="text-xs text-green-700">
          Loading: {profileLoading ? 'Yes' : 'No'} | Error: {profileError || 'None'}
        </p>
      </div>

      <div className="flex gap-3 mt-4 pt-4 border-t-2 border-gray-300">
        <button
          onClick={async () => {
            setLoading(true);
            try {
              const testMatch = httpsCallable(functions, 'testMatch');
              addResult('⏳ Running Matching Engine Test...');
              const result = await testMatch({});
              const data = result.data as any;

              if (data.success) {
                addResult('✅ Matching Engine Test Success!');
                data.scenarios.forEach((scenario: any) => {
                  const icon = scenario.result.isMatch ? '💚 MATCH' : '🛑 NO MATCH';
                  addResult(
                    `${icon}: ${scenario.name} | ${scenario.startup} <-> ${scenario.investor}`
                  );
                  if (!scenario.result.isMatch) {
                    // Log failure reason if available (currently logic returns generic false, but we can infer)
                    addResult(`   -> Score: ${scenario.result.score}`);
                  }
                });
              } else {
                addResult(`❌ Matching Engine Test Failed: ${data.error}`);
              }
            } catch (error) {
              addResult(`❌ Error calling testMatch: ${error}`);
            }
            setLoading(false);
          }}
          disabled={loading}
          className="px-6 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50 font-bold"
        >
          🧩 TEST MATCHING ENGINE
        </button>
      </div>

      {/* Tracking Tests Section */}
      <div className="mt-6 pt-6 border-t-2 border-gray-300">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          📊 Tracking Tests (TikTok Algorithm)
        </h3>

        {/* Startup ID Input */}
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Startup ID para testear (opcional, déjalo vacío para usar un ID de prueba):
          </label>
          <input
            type="text"
            value={testStartupId}
            onChange={e => setTestStartupId(e.target.value)}
            placeholder="Ej: startup-123 o déjalo vacío"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {!testInvestorUser && !authUser && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              ⚠️ Necesitas estar autenticado O crear un usuario investor de prueba.
              <br />
              Opción 1: Inicia sesión con un usuario investor existente
              <br />
              Opción 2: Usa el botón &quot;CREATE INVESTOR USER&quot; arriba para crear uno de
              prueba
            </p>
          </div>
        )}

        {authUser && authUser.userType !== 'investor' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800">
              ⚠️ El usuario autenticado no es un investor. Solo los investors pueden trackear
              interacciones.
              <br />
              Usuario actual: {authUser.email} (Tipo: {authUser.userType})
            </p>
          </div>
        )}

        {authUser && authUser.userType === 'investor' && !testInvestorUser && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-800">
              ✅ Usando usuario autenticado: {authUser.email} (ID: {authUser.id})
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {/* Test VIEW */}
          <button
            onClick={async () => {
              const userId = testInvestorUser?.id || authUser?.id;
              if (!userId || !firebaseUser) {
                addResult(
                  '❌ TRACK VIEW: Necesitas estar autenticado. Inicia sesión con un usuario investor.'
                );
                return;
              }
              if (authUser && authUser.userType !== 'investor') {
                addResult('❌ TRACK VIEW: El usuario autenticado debe ser un investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'view',
                  duration: 45000, // 45 segundos
                  source: 'catalog',
                  category: 'Your Matches',
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK VIEW: Interacción registrada - ID: ${data.interactionId}, Duración: 45s`
                  );
                } else {
                  addResult(`❌ TRACK VIEW: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK VIEW: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={
              loading ||
              (!testInvestorUser &&
                (!firebaseUser || !authUser || authUser.userType !== 'investor'))
            }
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            👁️ Track VIEW
          </button>

          {/* Test FAVORITE */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK FAVORITE: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'favorite',
                  source: 'catalog',
                  category: 'Your Matches',
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK FAVORITE: Interacción registrada - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK FAVORITE: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK FAVORITE: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
          >
            ❤️ Track FAVORITE
          </button>

          {/* Test SCROLL PROFILE */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK SCROLL PROFILE: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'scroll-profile',
                  metadata: { scrollDepth: 75 }, // 75% del perfil
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK SCROLL PROFILE: Interacción registrada - Scroll: 75% - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK SCROLL PROFILE: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK SCROLL PROFILE: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50"
          >
            📜 Track SCROLL PROFILE
          </button>

          {/* Test SCROLL PITCH DECK */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK SCROLL PITCH DECK: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'scroll-pitch-deck',
                  metadata: { scrollDepth: 50 }, // 50% del deck
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK SCROLL PITCH DECK: Interacción registrada - Scroll: 50% - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK SCROLL PITCH DECK: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK SCROLL PITCH DECK: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50"
          >
            📄 Track SCROLL PITCH DECK
          </button>

          {/* Test CLICK WEBSITE */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK CLICK WEBSITE: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'click-website',
                  metadata: { linkUrl: 'https://example-startup.com' },
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK CLICK WEBSITE: Interacción registrada - URL: example-startup.com - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK CLICK WEBSITE: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK CLICK WEBSITE: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
          >
            🌐 Track CLICK WEBSITE
          </button>

          {/* Test CLICK LINK */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK CLICK LINK: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'click-link',
                  metadata: {
                    linkUrl: 'https://linkedin.com/company/startup',
                    elementClicked: 'linkedin-link',
                  },
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK CLICK LINK: Interacción registrada - LinkedIn - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK CLICK LINK: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK CLICK LINK: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
          >
            🔗 Track CLICK LINK
          </button>

          {/* Test NEXT MATCH */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK NEXT MATCH: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              try {
                const trackInteraction = httpsCallable(functions, 'trackInteraction');
                const startupId = testStartupId || 'test-startup-123';
                const result = await trackInteraction({
                  userId: userId,
                  startupId: startupId,
                  action: 'next-match',
                  source: 'catalog',
                });
                const data = result.data as any;
                if (data.success) {
                  addResult(
                    `✅ TRACK NEXT MATCH: Interacción registrada - ID: ${data.interactionId}`
                  );
                } else {
                  addResult(`❌ TRACK NEXT MATCH: Error - ${data.error}`);
                }
              } catch (error) {
                addResult(`❌ TRACK NEXT MATCH: Error - ${error}`);
              }
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
          >
            ⏭️ Track NEXT MATCH
          </button>

          {/* Test ALL ACTIONS */}
          <button
            onClick={async () => {
              const userId = getTrackingUserId();
              if (!userId || !canTrack()) {
                addResult('❌ TRACK ALL: Necesitas estar autenticado como investor');
                return;
              }
              setLoading(true);
              addResult('🚀 Iniciando test completo de tracking...');
              const startupId = testStartupId || 'test-startup-123';
              const trackInteraction = httpsCallable(functions, 'trackInteraction');

              const actions = [
                { action: 'view', duration: 30000, label: 'VIEW (30s)' },
                {
                  action: 'scroll-profile',
                  metadata: { scrollDepth: 60 },
                  label: 'SCROLL PROFILE (60%)',
                },
                {
                  action: 'scroll-pitch-deck',
                  metadata: { scrollDepth: 40 },
                  label: 'SCROLL PITCH DECK (40%)',
                },
                {
                  action: 'click-website',
                  metadata: { linkUrl: 'https://example.com' },
                  label: 'CLICK WEBSITE',
                },
                { action: 'favorite', label: 'FAVORITE' },
                { action: 'next-match', label: 'NEXT MATCH' },
              ];

              for (const testAction of actions) {
                try {
                  const result = await trackInteraction({
                    userId: userId,
                    startupId: startupId,
                    action: testAction.action as any,
                    duration: testAction.duration,
                    metadata: testAction.metadata,
                    source: 'catalog',
                    category: 'Your Matches',
                  });
                  const data = result.data as any;
                  if (data.success) {
                    addResult(`✅ ${testAction.label}: OK - ID: ${data.interactionId}`);
                  } else {
                    addResult(`❌ ${testAction.label}: Error - ${data.error}`);
                  }
                } catch (error) {
                  addResult(`❌ ${testAction.label}: Error - ${error}`);
                }
                await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms between calls
              }
              addResult('🎉 Test completo de tracking finalizado!');
              setLoading(false);
            }}
            disabled={loading || !canTrack()}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 font-bold"
          >
            🚀 Test ALL TRACKING
          </button>
        </div>
      </div>

      {loading && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">⏳ Ejecutando función...</p>
        </div>
      )}

      <div className="bg-gray-50 border rounded p-4 max-h-96 overflow-y-auto">
        <h3 className="font-semibold mb-2 text-gray-700">📋 Resultados:</h3>
        {results.length === 0 ? (
          <p className="text-gray-500 italic">
            No hay resultados aún. Haz clic en algún botón para probar las funciones.
          </p>
        ) : (
          <div className="space-y-1">
            {results.map((result, index) => (
              <div key={index} className="text-sm font-mono bg-white p-2 rounded border">
                {result}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudFunctionsTest;
